import React, { useContext, useState } from "react";
import { gql } from "@apollo/client";
import {
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  ListItemButton,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { Home, Cog, DeskLamp } from "mdi-material-ui";

import logoImage from "images/logo_button.png";
import {
  UserIcon,
  CompanyIcon,
  FolderIcon,
  DocumentIcon,
  TeamIcon,
  ProjectIcon,
  DataQueryIcon,
  ChatIcon,
} from "controls";
import {
  userRoute,
  companyRoute,
  teamRoute,
  itemRoute,
  dataQueryRoute,
  picklistRoute,
  chatRoute,
  myRoute,
  folderRoute,
  projectsRoute,
  documentsRoute,
  chatsRoute,
  dataQueriesRoute,
  companiesRoute,
  usersRoute,
  superadminRoute,
  configureRoute,
  userSettingsRoute,
} from "helpers";
import { viewportContext } from "contexts";
import { useRoute, useData } from "hooks";

import { NAVBAR_HEIGHT } from "./Navbar";
import matchRoute from "helpers/matchRoute";

export const DRAWER_WIDTH = 240;
export const DENSE_DRAWER_WIDTH = 56;

export default function AppDrawer() {
  const { teamId } = useRoute(teamRoute);
  const location = useLocation();

  const data = useData(gql`
    query AppDrawer {
      userLogin {
        id
        isSuperAdmin
      }
    }
  `);

  const { denseView, drawerOpen, drawerOpenSet } = useContext(viewportContext);

  const drawerProps = {
    style: {},
    ...(!denseView
      ? {
          variant: "permanent",
        }
      : {
          variant: "temporary",
          open: drawerOpen,
          onClose: () => drawerOpenSet(false),
        }),
    PaperProps: {
      style: {
        whiteSpace: "nowrap",
        width: denseView ? DRAWER_WIDTH : DENSE_DRAWER_WIDTH,
        zIndex: 1000,
        overflow: "visible",
        paddingTop: `env(safe-area-inset-top)`,
        paddingBottom: `env(safe-area-inset-bottom)`,
      },
    },
  };

  return (
    <Drawer {...drawerProps}>
      <div
        style={{
          height: NAVBAR_HEIGHT,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <img
          alt="优建云"
          style={{
            width: 32,
            height: 32,
          }}
          src={logoImage}
        />
      </div>
      <DrawerMenu
        items={[
          {
            icon: <Home />,
            title: "团队首页",
            href: `/${teamId}`,
            selected: matchRoute(location.pathname, teamRoute, {
              exact: true,
            }),
          },
          {
            icon: <DeskLamp />,
            title: "工作台",
            href: `/${teamId}/my`,
            selected: matchRoute(location.pathname, myRoute),
          },
          {
            icon: <FolderIcon />,
            title: "目录",
            href: `/${teamId}/folders`,
            selected:
              matchRoute(location.pathname, folderRoute) ||
              matchRoute(location.pathname, itemRoute),
          },
          {
            icon: <ProjectIcon />,
            title: "项目",
            href: `/${teamId}/projects`,
            selected: matchRoute(location.pathname, projectsRoute),
          },
          {
            icon: <DocumentIcon />,
            title: "表单",
            href: `/${teamId}/documents`,
            selected: matchRoute(location.pathname, documentsRoute),
          },
          {
            icon: <ChatIcon />,
            title: "讨论",
            href: `/${teamId}/chats`,
            selected:
              matchRoute(location.pathname, chatsRoute) ||
              matchRoute(location.pathname, chatRoute),
          },
          {
            icon: <DataQueryIcon />,
            title: "统计",
            href: `/${teamId}/data_queries`,
            selected:
              matchRoute(location.pathname, dataQueriesRoute) ||
              matchRoute(location.pathname, dataQueryRoute),
          },
          {
            icon: <CompanyIcon />,
            title: "单位",
            href: `/${teamId}/companies`,
            selected:
              matchRoute(location.pathname, companiesRoute) ||
              matchRoute(location.pathname, companyRoute),
          },
          {
            icon: <UserIcon />,
            title: "成员",
            href: `/${teamId}/users`,
            selected:
              matchRoute(location.pathname, usersRoute) ||
              matchRoute(location.pathname, userRoute),
          },
        ]}
      />
      <div style={{ flex: "1 0 auto" }} />
      <DrawerMenu
        items={[
          data?.userLogin?.isSuperAdmin && {
            icon: <Cog />,
            title: "超级管理员工具",
            href: `/${teamId}/superadmin`,
            selected: matchRoute(location.pathname, superadminRoute),
          },
          {
            icon: <TeamIcon />,
            title: "团队设置",
            href: `/${teamId}/configure`,
            selected:
              matchRoute(location.pathname, configureRoute) ||
              matchRoute(location.pathname, picklistRoute),
          },
          {
            icon: <Cog />,
            title: "我的设置",
            href: `/${teamId}/user_settings`,
            selected: matchRoute(location.pathname, userSettingsRoute),
          },
        ].filter((t) => t)}
      />
    </Drawer>
  );
}

function DrawerMenu({ items }) {
  const [hovering, hoveringSet] = useState(false);
  const { denseView } = useContext(viewportContext);

  return (
    <Paper
      style={{
        position: "relative",
        borderRadius: 0,
        ...(hovering && {
          width: DRAWER_WIDTH,
          border: "inherit",
        }),
        ...(denseView && {
          width: DRAWER_WIDTH,
        }),
      }}
      elevation={0}
      onMouseEnter={() => hoveringSet(true)}
      onMouseLeave={() => hoveringSet(false)}
    >
      <Divider />
      <List disablePadding>
        {items.map((item) => (
          <ListItem
            disablePadding
            dense
            key={item.key || item.title}
            disabled={item.disabled}
            selected={!!item.selected}
            style={{
              borderLeft: `solid 2px ${
                item.selected ? "currentcolor" : "transparent"
              }`,
              overflow: "hidden",
            }}
          >
            <ListItemButton
              component="a"
              href={item.href || "#"}
              onClick={item.onClick}
            >
              <ListItemIcon style={{ minWidth: 48 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.title} style={{ margin: 0 }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
    </Paper>
  );
}
